/**
 * 試験区分マスタ（単一の正）
 *
 * これまで EXAM_INFO / EXAM_TYPES / YEARS / SEASONS が
 * ExamCategoryContext・AdminPage・FilterBar に重複定義されていた。
 * 新区分（IPA新制度の3領域: DM/PDS 等）の追加が「このファイル + DB」で
 * 完結するよう、区分に関する定義をここへ一元化する。
 *
 * - 区分そのものを増やす → EXAM_CATALOG に1行追加
 * - クイズ/管理のフィルタに出す区分を増やす → QUIZ_FILTER_CODES に code を追加
 */

import { ExamCategory } from "../types";
import { sectionLabel, sectionLabelWithSubject } from "./sectionLabels";

export interface ExamInfo {
  code: ExamCategory;
  name: string;
  shortName: string;
  level: number;
  isAdvanced: boolean;
  isBeta?: boolean; // AI 生成問題だけの新制度区分（β）。過去問は無い
}

/** 全区分マスタ。区分を増やすときはここに1行足す（順序も表示順の基準） */
export const EXAM_CATALOG: ExamInfo[] = [
  { code: "AP", name: "応用情報技術者", shortName: "AP", level: 3, isAdvanced: false },
  { code: "SC", name: "情報処理安全確保支援士", shortName: "SC", level: 4, isAdvanced: true },
  { code: "NW", name: "ネットワークスペシャリスト", shortName: "NW", level: 4, isAdvanced: true },
  { code: "DB", name: "データベーススペシャリスト", shortName: "DB", level: 4, isAdvanced: true },
  { code: "PM", name: "プロジェクトマネージャ", shortName: "PM", level: 4, isAdvanced: true },
  { code: "SA", name: "システムアーキテクト", shortName: "SA", level: 4, isAdvanced: true },
  { code: "AU", name: "システム監査技術者", shortName: "AU", level: 4, isAdvanced: true },
  { code: "ES", name: "エンベデッドシステムスペシャリスト", shortName: "ES", level: 4, isAdvanced: true },
  { code: "ST", name: "ITストラテジスト", shortName: "ST", level: 4, isAdvanced: true },
  { code: "SM", name: "ITサービスマネージャ", shortName: "SM", level: 4, isAdvanced: true },
  // 2027新制度の共通科目。収録はAI生成問題（β）のみ。QUIZ_FILTER_CODES への追加＝露出は Phase P-2
  { code: "A1", name: "新制度 科目A-1（共通知識）", shortName: "A-1", level: 4, isAdvanced: false, isBeta: true },
  { code: "PD-M", name: "新制度 プロフェッショナルデジタルスキル（マネジメント）", shortName: "PD-M", level: 4, isAdvanced: false, isBeta: true },
  { code: "PD-S", name: "新制度 プロフェッショナルデジタルスキル（システム）", shortName: "PD-S", level: 4, isAdvanced: false, isBeta: true },
  { code: "DM", name: "新制度 データマネジメント", shortName: "DM", level: 4, isAdvanced: false, isBeta: true },
];

/** code -> ExamInfo の索引 */
export const EXAM_INFO: Record<string, ExamInfo> = Object.fromEntries(
  EXAM_CATALOG.map((info) => [info.code, info])
);

/**
 * クイズ/管理のフィルタに表示する区分と順序。
 * フロントの区分拡張（Phase 3残）はこの配列に code を足すだけで反映される。
 */
// β 区分はカスタムのフィルタで選べる（design §8a）
export const QUIZ_FILTER_CODES: ExamCategory[] = ["AP", "SC", "NW", "DB", "A1", "PD-M", "PD-S", "DM"];

/** β 区分（AI 生成問題のみ）の code。表示順はマスタの順 */
export const BETA_EXAM_CODES: ExamCategory[] = EXAM_CATALOG.filter((e) => e.isBeta).map((e) => e.code);

export interface ExamTypeOption {
  value: string;
  label: string;
  section?: string;
}

/**
 * フィルタUI用のラベル。
 * 高度区分は「午前Ⅱ」(section=morning2)、それ以外は「午前」。
 */
function toExamTypeOption(info: ExamInfo): ExamTypeOption {
  if (info.isBeta) return { value: info.code, label: `${info.code} 科目A（β）` };
  const label = `${info.code} ${info.isAdvanced ? "午前Ⅱ" : "午前"}`;
  return info.isAdvanced
    ? { value: info.code, label, section: "morning2" }
    : { value: info.code, label };
}

/**
 * 出題画面・フィルタのセクション表記。
 * β 区分は 2027 年新制度の生成問題なので現行の『午前』を使わない。
 */
export function quizSectionLabel(
  code: string | undefined,
  section: string | undefined,
  withSubject = false
): string {
  if (code && EXAM_INFO[code]?.isBeta) return "科目A（β）";
  return withSubject ? sectionLabelWithSubject(section, true) : sectionLabel(section);
}

/** 「すべて」+ フィルタ対象区分。CustomQuizSetup / AdminPage 共通 */
export const EXAM_TYPES: ExamTypeOption[] = [
  { value: "", label: "すべて" },
  ...QUIZ_FILTER_CODES.map((code) => toExamTypeOption(EXAM_INFO[code])),
];

/** フィルタ用の年度リスト（新しい順） */
export const EXAM_YEARS = [2025, 2024, 2023, 2022, 2021, 2020, 2019, 2018, 2017, 2016, 2015];

/** 区分ごとの収録問題数（DB実測 2026-08-09）。データ追加時はここを更新 */
export const QUESTION_COUNTS: Record<string, number> = {
  AP: 960,
  SC: 325,
  NW: 225,
  DB: 250,
  PM: 232,
  SA: 200,
  AU: 250,
  ES: 249,
  ST: 192,
  SM: 217,
  // β の生成問題（27_promote_approved.py で 2026-10-31 に昇格した approved 全数）。過去問は無い。再昇格したらここを更新
  A1: 31,
  "PD-M": 29,
  "PD-S": 33,
  DM: 26,
};

/** 収録問題総数（β 区分を除く QUESTION_COUNTS の合計。全問AI解説付き）。β の生成問題は過去問の総数に含めない（マーケティングの数字と揃える） */
export const TOTAL_QUESTION_COUNT = EXAM_CATALOG.filter((e) => !e.isBeta).reduce(
  (sum, e) => sum + (QUESTION_COUNTS[e.code] ?? 0),
  0
);

/** 区分ごとの収録年度数（実施年度の実数・DB実測 2026-08-09。2020年度中止の回は含まない） */
export const YEARS_COVERAGE: Record<string, number> = {
  AP: 7,
  SC: 7,
  NW: 9,
  DB: 10,
  PM: 10,
  SA: 8,
  AU: 10,
  ES: 10,
  ST: 8,
  SM: 9,
  A1: 0, // 過去問なし（新制度は2027年開始）
  "PD-M": 0, // 同上
  "PD-S": 0, // 同上
  DM: 0, // 同上
};

/** フィルタ用の季節リスト */
export const EXAM_SEASONS: { value: string; label: string }[] = [
  { value: "spring", label: "春期" },
  { value: "autumn", label: "秋期" },
];
